import { useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useQuery } from '@tanstack/react-query';
import { RootState } from '@/store';
import { MemberStatus } from '@/types/member.types';
import { updateMemberStatus } from '@/store/authSlice';
import memberService from '@/services/member.service';
import { derivePaymentFailureState } from '@/utils/paymentFailureState';

const BLOCKED_STATUSES: MemberStatus[] = [
  MemberStatus.CANCELLED,
  MemberStatus.PAUSED,
  MemberStatus.PAYMENT_FAILED,
  MemberStatus.PAST_DUE,
  MemberStatus.SUSPENDED,
];

const MEMBER_WAS_BLOCKED_KEY = 'memberWasBlocked';

export function useMemberAccess() {
  const dispatch = useDispatch();
  const reduxUser = useSelector((state: RootState) => state.auth.user);

  // Fetch fresh membership status whenever this hook mounts on a guarded route.
  // staleTime: 30s — avoids repeated hits when navigating multi-step booking flows,
  // but catches status changes that occurred while the user was idle on home.
  const { data: memberProfile, isLoading } = useQuery({
    queryKey: ['memberProfile'],
    queryFn: () => memberService.getMemberProfile(),
    staleTime: 30 * 1000,
    enabled: !!reduxUser,
  });

  // Keep Redux + localStorage in sync with the freshly fetched status
  useEffect(() => {
    if (memberProfile?.status) {
      dispatch(updateMemberStatus(memberProfile.status));
    }
  }, [memberProfile?.status, dispatch]);

  // Use the fresh profile status once available; fall back to Redux while loading
  const status = (memberProfile?.status ?? reduxUser?.status) as MemberStatus | undefined;

  const paymentFailure = derivePaymentFailureState({
    status,
    subscriptionStatus: memberProfile?.subscriptionStatus,
    subscriptionStatusChangedAt: memberProfile?.subscriptionStatusChangedAt,
  });

  // Day 0-7 of a payment failure is a non-blocking countdown warning — the
  // trailing exemption keeps grace-phase members off the block wall even
  // when a location's GHL also sets membership status to payment_failed.
  const isAccessBlocked =
    ((!!status && BLOCKED_STATUSES.includes(status)) ||
      paymentFailure.phase === 'suspended' ||
      paymentFailure.phase === 'ended') &&
    paymentFailure.phase !== 'grace';

  // past_due gets its own treatment: instead of a generic "contact your Box Pro"
  // wall, the member is shown a self-serve payment-method update flow.
  const isPastDue = status === MemberStatus.PAST_DUE;

  // Detect reactivation: was previously blocked, now active
  const wasReactivated =
    status === MemberStatus.ACTIVE &&
    localStorage.getItem(MEMBER_WAS_BLOCKED_KEY) === 'true';

  useEffect(() => {
    if (status && BLOCKED_STATUSES.includes(status)) {
      localStorage.setItem(MEMBER_WAS_BLOCKED_KEY, 'true');
    }
  }, [status]);

  const clearReactivationFlag = () => {
    localStorage.removeItem(MEMBER_WAS_BLOCKED_KEY);
  };

  // isChecking is true only on the very first load when there's no cached data yet
  const isChecking = isLoading && !memberProfile;

  return {
    status,
    isAccessBlocked,
    isPastDue,
    wasReactivated,
    clearReactivationFlag,
    isChecking,
    paymentFailure,
  };
}
